import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { SuperAdminBadge } from '@/shared/components/SuperAdminBadge';
import { PortalAccessGate } from '@/shared/components/PortalAccessGate';
import InlineErrorBoundary from '@/components/system/InlineErrorBoundary';
import AgentProfileDropdown from '../components/AgentProfileDropdown';
import AgentNotificationsBell from '../components/AgentNotificationsBell';
import khLogo from '@/assets/kh-logo-square.png';

export default function AgentLayout() {
  const navCls = ({ isActive }: { isActive: boolean }) =>
    `text-xs px-2.5 py-1.5 rounded-md transition-colors ${
      isActive ? 'bg-primary-foreground/20 text-primary-foreground' : 'text-primary-foreground/70 hover:bg-primary-foreground/10'
    }`;
  // Submit Leads needs an agent_code; master/admin users without an agent_profiles row don't have one.
  const [hasAgentCode, setHasAgentCode] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      const uid = u.user?.id;
      if (!uid) return;
      const { data } = await supabase.from('agent_profiles').select('agent_code').eq('user_id', uid).maybeSingle();
      if (alive) setHasAgentCode(!!data?.agent_code);
    })();
    return () => { alive = false; };
  }, []);
  return (
    <div className="min-h-screen w-full bg-muted/30">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-primary text-primary-foreground border-b border-primary-foreground/10">
        <div className="h-12 flex items-center px-3 sm:px-4 lg:px-6 gap-3">
          <Link to="/agent/cases" className="flex items-center gap-2 shrink-0">
            <img src={khLogo} alt="MedTour" className="h-7 w-7 object-contain" />
            <span className="text-sm font-semibold tracking-wide hidden sm:inline">Agent Portal</span>
          </Link>

          <nav className="flex items-center gap-1 ml-2">
            <NavLink to="/agent/cases" className={navCls}>My Cases</NavLink>
            {hasAgentCode && <NavLink to="/agent/submit-leads" className={navCls}>Submit Leads</NavLink>}
            <NavLink to="/agent/my-submissions" className={navCls}>My Submissions</NavLink>
          </nav>

          <SuperAdminBadge portal="agent_portal" className="hidden sm:inline-flex" />
          <div className="flex-1" />

          <AgentNotificationsBell />
          <AgentProfileDropdown />
        </div>
      </header>

      {/* Content */}
      <main className="w-full max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4">
        <InlineErrorBoundary title="Agent page crashed">
          <PortalAccessGate portal="agent_portal"><Outlet /></PortalAccessGate>
        </InlineErrorBoundary>
      </main>
    </div>
  );
}
